// Per-user low-vision affordances. Persists to localStorage and applies
// to <html> via CSS variables / data-attributes — see the matching
// rules at the bottom of src/index.css ("Reading preferences").
//
// Exposes:
//   * fontScale          — 0.9 / 1.0 / 1.1 / 1.25 / 1.5
//   * lineHeight         — 1.4 / 1.5 / 1.7 / 1.9
//   * letterSpacing      — "normal" | "0.02em" | "0.05em"
//   * underlineLinks     — boolean (forces text-decoration on <a>)
//
// Why CSS variables on <html>:
//   font-size on html re-bases every rem-based size in the tree, which
//   is the WCAG 1.4.4 recommended "Resize text" hook. Setting it via a
//   variable instead of inline style means the user can scope their
//   choice to a theme (e.g. always 1.25× when in high-contrast) by
//   composing CSS rules later, without us having to thread state.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const STORAGE_KEY = "writeon:reading-prefs";

// eslint-disable-next-line react-refresh/only-export-components
export const FONT_SCALES = [
  { value: 0.9, label: "Smaller (90%)" },
  { value: 1.0, label: "Default (100%)" },
  { value: 1.1, label: "Larger (110%)" },
  { value: 1.25, label: "Large (125%)" },
  { value: 1.5, label: "Largest (150%)" },
];

// eslint-disable-next-line react-refresh/only-export-components
export const LINE_HEIGHTS = [
  { value: 1.4, label: "Tight" },
  { value: 1.5, label: "Default" },
  { value: 1.7, label: "Relaxed" },
  { value: 1.9, label: "Loose" },
];

// eslint-disable-next-line react-refresh/only-export-components
export const LETTER_SPACINGS = [
  { value: "normal", label: "Default" },
  { value: "0.02em", label: "Wide" },
  { value: "0.05em", label: "Wider" },
];

const DEFAULTS = {
  fontScale: 1.0,
  lineHeight: 1.5,
  letterSpacing: "normal",
  underlineLinks: false,
};

function load() {
  if (typeof window === "undefined") return DEFAULTS;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULTS;
    const parsed = JSON.parse(raw);
    return { ...DEFAULTS, ...parsed };
  } catch {
    return DEFAULTS;
  }
}

function apply(prefs) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.setProperty("--reading-font-scale", String(prefs.fontScale));
  root.style.setProperty("--reading-line-height", String(prefs.lineHeight));
  root.style.setProperty("--reading-letter-spacing", String(prefs.letterSpacing));
  if (prefs.underlineLinks) {
    root.setAttribute("data-reading-underline-links", "on");
  } else {
    root.removeAttribute("data-reading-underline-links");
  }
}

const ReadingPrefsContext = createContext({
  prefs: DEFAULTS,
  setPref: () => {},
  reset: () => {},
});

export function ReadingPrefsProvider({ children }) {
  const [prefs, setPrefs] = useState(load);

  useEffect(() => {
    apply(prefs);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      /* storage disabled */
    }
  }, [prefs]);

  const setPref = useCallback((key, value) => {
    setPrefs((prev) => ({ ...prev, [key]: value }));
  }, []);

  const reset = useCallback(() => setPrefs(DEFAULTS), []);

  const value = useMemo(() => ({ prefs, setPref, reset }), [prefs, setPref, reset]);
  return (
    <ReadingPrefsContext.Provider value={value}>
      {children}
    </ReadingPrefsContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useReadingPrefs() {
  return useContext(ReadingPrefsContext);
}
